import { useState } from 'react';
import { Eye, EyeOff, KeyRound, Loader2, LogIn, User } from 'lucide-react';
import { useAuth } from '@/store/auth';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Input';
import { cn } from '@/lib/utils';

export function LoginScreen() {
  const login = useAuth((s) => s.login);
  const error = useAuth((s) => s.loginError);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    await login(username, password);
    setBusy(false);
  }

  return (
    <div className="grid h-full place-items-center bg-base p-4 sm:p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-[360px] rounded-lg border border-border-default bg-surface-1 p-5 shadow-2xl sm:p-6"
      >
        <div className="mb-5 flex flex-col items-center gap-2">
          <img src="/brand/logo-mark-light.svg" alt="" className="h-6 w-7 opacity-90" />
          <h1 className="text-base font-semibold tracking-tight text-fg-primary">Console</h1>
          <p className="text-[10px] uppercase tracking-[0.16em] text-fg-muted">
            powered by fabricOS
          </p>
        </div>
        <div className="space-y-3">
          <div>
            <Label htmlFor="u">Username</Label>
            <IconField
              icon={<User className="h-3.5 w-3.5" />}
            >
              <input
                id="u"
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className={inputCls}
              />
            </IconField>
          </div>
          <div>
            <Label htmlFor="p">Password</Label>
            <IconField
              icon={<KeyRound className="h-3.5 w-3.5" />}
              trailing={
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPw((v) => !v)}
                  className="grid h-7 w-7 place-items-center text-fg-muted hover:text-fg-primary"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              }
            >
              <input
                id="p"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={cn(inputCls, 'pr-9')}
              />
            </IconField>
          </div>
          {error && (
            <div className="rounded border border-status-critical/40 bg-status-critical/10 p-2 text-xs text-status-critical">
              {error}
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />}
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </div>
      </form>
    </div>
  );
}

const inputCls = cn(
  'h-9 w-full rounded-sm border border-border-default bg-base pl-8 pr-3 text-[13px]',
  'text-fg-primary placeholder:text-fg-disabled outline-none',
  'focus:border-accent transition-colors',
);

function IconField({
  icon,
  trailing,
  children,
}: {
  icon: React.ReactNode;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted">
        {icon}
      </span>
      {children}
      {trailing && (
        <span className="absolute right-1 top-1/2 -translate-y-1/2">{trailing}</span>
      )}
    </div>
  );
}
